import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import { INotification } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import { isSupplierPortalUser } from '../../lib/permissions';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'Ahora';
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  return `Hace ${Math.floor(hours / 24)} d`;
}

interface Props {
  onClose: () => void;
}

export function NotificationDropdown({ onClose }: Props) {
  const navigate = useNavigate();
  // El proveedor no tiene acceso al árbol interno: su bandeja completa vive dentro de su portal.
  const allPath = useAuth((s) => (isSupplierPortalUser(s.user) ? '/proveedor/notificaciones' : '/notifications'));
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications({ page: 1, limit: 10 });

  function handleClick(n: INotification) {
    if (!n.read) markRead(n.id);
    if (n.url) navigate(n.url);
    onClose();
  }

  return (
    <div style={{
      position: 'absolute',
      top: '100%',
      right: 0,
      marginTop: 8,
      width: 360,
      background: 'var(--color-surface)',
      borderRadius: 14,
      boxShadow: '0 8px 32px rgba(40,60,45,0.14)',
      border: '1px solid var(--color-border)',
      zIndex: 100,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '13px 16px',
        borderBottom: '1px solid var(--color-border-divider)',
      }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--color-text)' }}>Notificaciones</span>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllRead(undefined)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--color-primary-light)', fontWeight: 600 }}
          >
            Marcar todas como leídas
          </button>
        )}
      </div>

      {/* List */}
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {notifications.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--color-text-subtle)', fontSize: 13 }}>
            Sin notificaciones
          </div>
        ) : (
          notifications.map((n) => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 2,
                padding: '12px 16px',
                background: n.read ? 'var(--color-surface)' : 'var(--color-success-tint)',
                border: 'none',
                borderBottom: '1px solid var(--color-border-divider)',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.1s',
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = n.read ? 'var(--color-surface-alt)' : 'var(--color-success-bg)'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = n.read ? 'var(--color-surface)' : 'var(--color-success-tint)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                {!n.read && (
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-success)', flexShrink: 0 }} />
                )}
                <span style={{
                  fontSize: 13,
                  fontWeight: n.read ? 500 : 700,
                  color: 'var(--color-text)',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {n.title}
                </span>
                <span style={{ fontSize: 11, color: 'var(--color-icon-muted)', flexShrink: 0 }}>{timeAgo(n.createdAt)}</span>
              </div>
              <span style={{
                fontSize: 12,
                color: 'var(--color-text-muted)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                paddingLeft: n.read ? 0 : 14,
              }}>
                {n.message}
              </span>
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 16px', borderTop: '1px solid var(--color-border-divider)', textAlign: 'center' }}>
        <button
          onClick={() => { navigate(allPath); onClose(); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-primary-light)', fontWeight: 600 }}
        >
          Ver todas las notificaciones →
        </button>
      </div>
    </div>
  );
}
